/**
 * Shared time window for the device Analytics tab.
 *
 * The three panels (uptime, metrics, events) used to each own a range: Uptime
 * offered 7d/30d, Metrics 1h–30d, Events was pinned to 7 days. That made the
 * panels describe different periods at the same time, so a CPU spike could not
 * be lined up with the reboot that caused it. The container resolves ONE window
 * and hands it down, which also means the panels' query keys agree and
 * TanStack Query dedupes the overlapping requests.
 */

export type AnalyticsRangeKey = '1h' | '6h' | '24h' | '7d' | '30d';

export interface AnalyticsRangeOption {
  key: AnalyticsRangeKey;
  label: string;
  ms: number;
  /** Server-side aggregation bucket for the CPU/memory series. */
  interval: string;
}

export const ANALYTICS_RANGES: AnalyticsRangeOption[] = [
  { key: '1h', label: 'Last 1h', ms: 60 * 60 * 1000, interval: '1m' },
  { key: '6h', label: 'Last 6h', ms: 6 * 60 * 60 * 1000, interval: '5m' },
  { key: '24h', label: 'Last 24h', ms: 24 * 60 * 60 * 1000, interval: '15m' },
  { key: '7d', label: 'Last 7d', ms: 7 * 24 * 60 * 60 * 1000, interval: '1h' },
  { key: '30d', label: 'Last 30d', ms: 30 * 24 * 60 * 60 * 1000, interval: '6h' },
];

/**
 * CADENCE_MIN_INTERVAL_MS mirrors the server's metrics_agg_interval (#359):
 * device_metrics is aggregated into 30-min buckets, so heartbeat cadence can't
 * resolve finer than that without showing phantom gaps.
 */
const CADENCE_MIN_INTERVAL_MS = 30 * 60 * 1000;

/** The cadence API caps windows at 14d, matching retention. */
export const CADENCE_MAX_MS = 14 * 24 * 60 * 60 * 1000;
export const CADENCE_MAX_LABEL = '14 days';

/** Parses the bucket-interval strings used above ('1m'/'15m'/'1h'/…). */
export function intervalMs(s: string): number {
  const m = /^(\d+)([mhd])$/.exec(s);
  if (!m) return 0;
  const n = Number(m[1]);
  return n * (m[2] === 'm' ? 60_000 : m[2] === 'h' ? 3_600_000 : 86_400_000);
}

/** Picks a readable aggregation bucket for an arbitrary custom span. */
export function intervalForSpan(spanMs: number): string {
  const hours = spanMs / (60 * 60 * 1000);
  if (hours <= 2) return '1m';
  if (hours <= 12) return '5m';
  if (hours <= 48) return '15m';
  if (hours <= 24 * 14) return '1h';
  if (hours <= 24 * 60) return '6h';
  return '24h';
}

export interface AnalyticsWindow {
  key: AnalyticsRangeKey | 'custom';
  label: string;
  start: string;
  end: string;
  spanMs: number;
  /** Bucket for CPU/memory. */
  interval: string;
  /** Bucket for cadence — floored at the server's 30-min aggregation. */
  cadenceInterval: string;
  /** False when the window exceeds the cadence API's 14-day cap. */
  cadenceEligible: boolean;
  /** Long-form axis labels (include the date) once the span passes a day. */
  longForm: boolean;
}

function build(
  key: AnalyticsWindow['key'],
  label: string,
  start: string,
  end: string,
  interval: string,
): AnalyticsWindow {
  const spanMs = new Date(end).getTime() - new Date(start).getTime();
  return {
    key,
    label,
    start,
    end,
    spanMs,
    interval,
    cadenceInterval: intervalMs(interval) >= CADENCE_MIN_INTERVAL_MS ? interval : '30m',
    cadenceEligible: spanMs > 0 && spanMs <= CADENCE_MAX_MS,
    longForm: spanMs >= 24 * 60 * 60 * 1000,
  };
}

/**
 * Resolves a canned range to a concrete window. Call this inside a useMemo keyed
 * on the range key — it reads the clock, so calling it during render would
 * produce a new window (and a new query key) on every pass.
 */
export function resolveWindow(key: AnalyticsRangeKey): AnalyticsWindow {
  const opt = ANALYTICS_RANGES.find((r) => r.key === key) ?? ANALYTICS_RANGES[3];
  const now = Date.now();
  return build(
    opt.key,
    opt.label,
    new Date(now - opt.ms).toISOString(),
    new Date(now).toISOString(),
    opt.interval,
  );
}

/** Resolves an operator-supplied start/end pair. */
export function resolveCustomWindow(start: string, end: string): AnalyticsWindow {
  const span = new Date(end).getTime() - new Date(start).getTime();
  return build('custom', 'Custom range', start, end, intervalForSpan(span));
}
